import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { descargarArchivo } from "../../utils/download";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import {
  Search, Filter, CheckCircle, XCircle, Clock, MessageCircle,
  DollarSign, Plus, CreditCard, AlertCircle, Download, Edit, Trash2, History
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";
import ImgFallback from "../../components/ImgFallback";
import HistorialPanel from "../../components/HistorialPanel";

const Pagos = () => {
  const [pagos, setPagos] = useState([]);
  const [clientes, setClientes] = useState({});
  const [clientesList, setClientesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEstatus, setFilterEstatus] = useState("todos");
  const [searchTerm, setSearchTerm] = useState("");
  const [incluirCancelados, setIncluirCancelados] = useState(false);
  const [pagoDialog, setPagoDialog] = useState(false);
  const [registrarDialog, setRegistrarDialog] = useState(null);
  const [registrarMonto, setRegistrarMonto] = useState("");
  const [registrarMetodo, setRegistrarMetodo] = useState("transferencia");
  const [registrarNotas, setRegistrarNotas] = useState("");
  const [registrarComprobante, setRegistrarComprobante] = useState("");
  const [subiendoComprobante, setSubiendoComprobante] = useState(false);
  const [newPago, setNewPago] = useState({ cliente_id: "", monto_total: "", monto_pagado: "", metodo: "transferencia", notas: "" });
  const [editDialog, setEditDialog] = useState(null);
  const [editData, setEditData] = useState({ monto_total: "", metodo: "transferencia", notas: "" });
  const [cancelarDialog, setCancelarDialog] = useState(null);
  const [motivoCancelacion, setMotivoCancelacion] = useState("");
  const [historialDialog, setHistorialDialog] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [pagosRes, clientesRes] = await Promise.all([
        axios.get(`${API}/pagos`, { params: { incluir_cancelados: incluirCancelados } }),
        axios.get(`${API}/clientes`)
      ]);
      setPagos(pagosRes.data);
      const clientesMap = {};
      clientesRes.data.forEach(c => { clientesMap[c.cliente_id] = c; });
      setClientes(clientesMap);
      setClientesList(clientesRes.data);
    } catch {
      toast.error("Error al cargar los pagos");
    } finally {
      setLoading(false);
    }
  }, [incluirCancelados]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCrearPago = async () => {
    if (!newPago.cliente_id || !newPago.monto_total) {
      toast.error("Cliente y monto son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/pagos`, {
        cliente_id: newPago.cliente_id,
        monto: parseFloat(newPago.monto_total),
        monto_total: parseFloat(newPago.monto_total),
        monto_pagado: parseFloat(newPago.monto_pagado || 0),
        metodo: newPago.metodo,
        notas: newPago.notas,
      });
      toast.success("Pago creado y saldos sincronizados");
      setPagoDialog(false);
      setNewPago({ cliente_id: "", monto_total: "", monto_pagado: "", metodo: "transferencia", notas: "" });
      await fetchData();
    } catch (error) {
      toast.error("Error al crear pago");
    }
  };

  const handleSubirComprobante = async (file) => {
    if (!file) return null;
    setSubiendoComprobante(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data?.url || "";
      setRegistrarComprobante(url);
      toast.success("Comprobante subido");
      return url;
    } catch {
      toast.error("No se pudo subir el comprobante");
      return null;
    } finally { setSubiendoComprobante(false); }
  };

  const handleRegistrarPago = async () => {
    if (!registrarMonto || parseFloat(registrarMonto) <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    try {
      await axios.put(`${API}/pagos/${registrarDialog.pago_id}`, {
        registrar_pago: parseFloat(registrarMonto),
        metodo: registrarMetodo,
        notas: registrarNotas,
        comprobante_url: registrarComprobante || undefined,
      });
      toast.success("Abono registrado y saldos actualizados");
      setRegistrarDialog(null);
      setRegistrarMonto("");
      setRegistrarNotas("");
      setRegistrarComprobante("");
      await fetchData();
    } catch (error) {
      toast.error("Error al registrar pago");
    }
  };

  const handleAprobar = async (pagoId) => {
    try {
      await axios.post(`${API}/pagos/${pagoId}/aprobar`);
      toast.success("Pago aprobado · evento confirmado");
      await fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al aprobar"); }
  };

  const handleRechazar = async (pago) => {
    const motivo = window.prompt(`Rechazar pago de ${pago.pago_id?.slice(-6)}? Motivo (opcional):`) ?? null;
    if (motivo === null) return;
    try {
      await axios.post(`${API}/pagos/${pago.pago_id}/rechazar`, { motivo });
      toast.success("Pago rechazado y evento liberado");
      await fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al rechazar"); }
  };

  const handleValidar = async (pagoId) => {
    try {
      await axios.put(`${API}/pagos/${pagoId}`, { estatus: "liquidado" });
      toast.success("Pago marcado como liquidado");
      await fetchData();
    } catch { toast.error("Error"); }
  };

  const openEditPago = (pago) => {
    setEditDialog(pago);
    setEditData({
      monto_total: pago.monto_total || pago.monto || 0,
      metodo: pago.metodo || "transferencia",
      notas: pago.notas || ""
    });
  };

  const handleEditPago = async () => {
    if (!editDialog) return;
    try {
      await axios.put(`${API}/pagos/${editDialog.pago_id}`, {
        monto_total: parseFloat(editData.monto_total) || 0,
        metodo: editData.metodo,
        notas: editData.notas
      });
      toast.success("Pago actualizado");
      setEditDialog(null);
      await fetchData();
    } catch { toast.error("Error al actualizar pago"); }
  };

  const handleCancelarPago = async () => {
    if (!cancelarDialog) return;
    if (!motivoCancelacion.trim()) {
      toast.error("Indica el motivo de cancelación");
      return;
    }
    try {
      await axios.post(`${API}/pagos/${cancelarDialog.pago_id}/cancelar`, {
        motivo: motivoCancelacion.trim(),
      });
      toast.success("Pago cancelado. Permanece en historial.");
      setCancelarDialog(null);
      setMotivoCancelacion("");
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cancelar pago");
    }
  };

  const filteredPagos = pagos.filter(pago => {
    const cliente = clientes[pago.cliente_id];
    const nombre = cliente?.nombre || pago.cliente_nombre || "";
    const whatsapp = cliente?.whatsapp || pago.cliente_whatsapp || "";
    const matchesSearch = !searchTerm || nombre.toLowerCase().includes(searchTerm.toLowerCase()) || whatsapp.includes(searchTerm) || pago.pago_id?.includes(searchTerm);
    const matchesStatus = filterEstatus === "todos" || pago.estatus === filterEstatus || (filterEstatus === "cancelado" && pago.cancelado);
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (pago) => {
    if (pago.cancelado) return { label: "Cancelado", cls: "bg-red-100 text-red-700" };
    const status = pago.estatus;
    const map = {
      pendiente_aprobacion: { label: "Por aprobar", cls: "bg-amber-200 text-amber-900 font-medium" },
      pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
      parcial: { label: "Parcial", cls: "bg-blue-100 text-blue-800" },
      aprobado: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-800" },
      liquidado: { label: "Liquidado", cls: "bg-rose-100 text-rose-800" },
      validado: { label: "Validado", cls: "bg-rose-100 text-rose-800" },
      rechazado: { label: "Rechazado", cls: "bg-red-100 text-red-800" },
    };
    return map[status] || { label: status, cls: "bg-stone-100 text-stone-600" };
  };

  const activos = pagos.filter(p => !p.cancelado);
  const porAprobar = activos.filter(p => p.estatus === "pendiente_aprobacion").length;
  const montoPorAprobar = activos.filter(p => p.estatus === "pendiente_aprobacion").reduce((s, p) => s + (p.monto_pagado || 0), 0);
  const pendientes = activos.filter(p => p.estatus === "pendiente").length;
  const parciales = activos.filter(p => p.estatus === "parcial").length;
  const liquidados = activos.filter(p => ["liquidado", "validado", "aprobado"].includes(p.estatus)).length;
  const totalRecaudado = activos.reduce((s, p) => s + (p.monto_pagado || 0), 0);
  const totalPendiente = activos.filter(p => ["pendiente", "parcial"].includes(p.estatus)).reduce((s, p) => s + Math.max((p.monto_total || p.monto || 0) - (p.monto_pagado || 0), 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-stone-900">Pagos</h1>
          <p className="text-stone-600 mt-1">Control de pagos y cobros</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            toast.info("Generando PDF de pagos...");
            try {
              const res = await axios.get(`${API}/admin/exportar/pdf/pagos`);
              descargarArchivo(res.data.url, res.data.filename);
              toast.success("PDF descargado");
            } catch { toast.error("Error al generar PDF"); }
          }} className="border-stone-300" data-testid="btn-pdf-pagos">
            <Download className="w-4 h-4 mr-2" /> Descargar PDF
          </Button>
          <Button onClick={() => setPagoDialog(true)} className="btn-primary" data-testid="btn-nuevo-pago">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Pago
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-stone-200">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Pendientes</p>
            <p className="font-display text-2xl text-amber-700">{pendientes}</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Parciales</p>
            <p className="font-display text-2xl text-blue-700">{parciales}</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Liquidados</p>
            <p className="font-display text-2xl text-rose-700">{liquidados}</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200 bg-rose-900">
          <CardContent className="p-4">
            <p className="text-xs text-rose-200 uppercase tracking-wider">Recaudado</p>
            <p className="font-display text-xl text-white">${totalRecaudado.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200 bg-amber-50">
          <CardContent className="p-4">
            <p className="text-xs text-amber-700 uppercase tracking-wider">Por cobrar</p>
            <p className="font-display text-xl text-amber-900">${totalPendiente.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input placeholder="Buscar por cliente..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 border-stone-300" data-testid="search-pagos" />
        </div>
        <Select value={filterEstatus} onValueChange={setFilterEstatus}>
          <SelectTrigger className="w-full md:w-48 border-stone-300" data-testid="filter-pagos">
            <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="parcial">Parciales</SelectItem>
            <SelectItem value="liquidado">Liquidados</SelectItem>
            <SelectItem value="cancelado">Cancelados</SelectItem>
            <SelectItem value="rechazado">Rechazados</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-stone-600 px-2">
          <input
            type="checkbox"
            checked={incluirCancelados}
            onChange={(e) => setIncluirCancelados(e.target.checked)}
            className="rounded"
            data-testid="toggle-incluir-cancelados"
          />
          Mostrar cancelados
        </label>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-rose-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredPagos.length > 0 ? (
        <div className="space-y-4">
          {filteredPagos.map((pago) => {
            const cliente = clientes[pago.cliente_id];
            // Iter109 · Fallback a snapshot enriquecido del backend cuando el cliente_id está huérfano
            const nombreCliente = cliente?.nombre || pago.cliente_nombre || "Cliente sin registrar";
            const whatsappCliente = cliente?.whatsapp || pago.cliente_whatsapp || "";
            const tipoEvento = cliente?.tipo_evento || pago.cliente_tipo_evento || "";
            const festejado = cliente?.nombre_festejado || pago.cliente_nombre_festejado || "";
            const badge = getStatusBadge(pago);
            const montoTotal = pago.monto_total || pago.monto || 0;
            const montoPagado = pago.monto_pagado || 0;
            const porcentaje = montoTotal > 0 ? Math.min(100, Math.round((montoPagado / montoTotal) * 100)) : 0;

            return (
              <Card key={pago.pago_id} className="border-stone-200" data-testid={`pago-card-${pago.pago_id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-medium text-stone-900">{nombreCliente}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                      </div>
                      <p className="text-xs text-stone-500">
                        {tipoEvento} {festejado ? `- ${festejado}` : ''} {whatsappCliente ? `| ${whatsappCliente}` : ''} {pago.fecha_evento ? `| Evento: ${pago.fecha_evento}` : ''}
                      </p>
                      {/* Progress bar */}
                      <div className="mt-3 flex items-center gap-3">
                        <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                          <div className="h-full bg-rose-600 rounded-full transition-all" style={{ width: `${porcentaje}%` }} />
                        </div>
                        <span className="text-xs text-stone-500 w-12 text-right">{porcentaje}%</span>
                      </div>
                      <div className="flex gap-4 mt-1 text-xs text-stone-500">
                        <span>Pagado: <strong className="text-rose-700">${montoPagado.toLocaleString()}</strong></span>
                        <span>Total: <strong>${montoTotal.toLocaleString()}</strong></span>
                        <span>Restante: <strong className="text-amber-700">${Math.max(0, montoTotal - montoPagado).toLocaleString()}</strong></span>
                      </div>
                      {/* Historial */}
                      {pago.historial_pagos?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {pago.historial_pagos.map((h, i) => (
                            <div key={h.fecha || `hist-${i}`} className="text-xs text-stone-400 flex items-center gap-2 flex-wrap">
                              <span>{new Date(h.fecha).toLocaleDateString('es-MX')}</span>
                              <span className="text-rose-600">+${h.monto.toLocaleString()}</span>
                              <span>{h.metodo}</span>
                              {h.notas && <span className="italic">({h.notas})</span>}
                              {h.comprobante_url && (
                                <a href={h.comprobante_url} target="_blank" rel="noreferrer" className="inline-block w-8 h-8 rounded overflow-hidden border border-stone-200" title="Ver comprobante">
                                  <ImgFallback
                                    src={h.comprobante_url}
                                    alt="comprobante"
                                    className="w-full h-full object-cover"
                                    fallback={<span className="text-[10px] text-amber-700 flex items-center justify-center w-full h-full">📎</span>}
                                  />
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Comprobante principal del pago */}
                      {pago.comprobante_url && (
                        <a href={pago.comprobante_url} target="_blank" rel="noreferrer" className="inline-block mt-2 w-20 h-20 rounded-lg border border-stone-200 overflow-hidden hover:border-rose-400" title="Ver comprobante" data-testid={`comprobante-pago-${pago.pago_id}`}>
                          <ImgFallback
                            src={pago.comprobante_url}
                            alt="comprobante"
                            className="w-full h-full object-cover"
                            fallbackClassName="w-full h-full flex items-center justify-center text-[10px] text-amber-700 bg-amber-50"
                            fallback={<div className="w-full h-full flex items-center justify-center text-[10px] text-amber-700 bg-amber-50">📎 ver</div>}
                          />
                        </a>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {!pago.cancelado && pago.estatus === "pendiente_aprobacion" && (
                        <>
                          <Button size="sm" onClick={() => handleAprobar(pago.pago_id)} className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs" data-testid={`btn-aprobar-${pago.pago_id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Aprobar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleRechazar(pago)} className="border-red-300 text-red-700 text-xs" data-testid={`btn-rechazar-${pago.pago_id}`}>
                            Rechazar
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setHistorialDialog(pago)} className="border-stone-200 h-8 w-8 p-0" title="Historial" data-testid={`btn-historial-pago-${pago.pago_id}`}>
                        <History className="w-3.5 h-3.5" />
                      </Button>
                      {!pago.cancelado && (
                        <Button size="sm" variant="outline" onClick={() => openEditPago(pago)} className="border-stone-200 h-8 w-8 p-0" title="Editar" data-testid={`btn-edit-pago-${pago.pago_id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {!pago.cancelado && pago.estatus !== "liquidado" && pago.estatus !== "rechazado" && pago.estatus !== "validado" && (
                        <Button size="sm" onClick={() => { setRegistrarDialog(pago); setRegistrarMonto(""); setRegistrarNotas(""); }} className="btn-primary text-xs" data-testid={`btn-registrar-${pago.pago_id}`}>
                          <CreditCard className="w-3 h-3 mr-1" /> Registrar pago
                        </Button>
                      )}
                      {!pago.cancelado && pago.estatus === "pendiente" && (
                        <Button size="sm" variant="outline" onClick={() => handleValidar(pago.pago_id)} className="border-rose-300 text-rose-700 text-xs" data-testid={`btn-validar-${pago.pago_id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Liquidar
                        </Button>
                      )}
                      {cliente?.whatsapp || pago.cliente_whatsapp ? (
                        <a href={`https://wa.me/52${(cliente?.whatsapp || pago.cliente_whatsapp || "").replace(/\D/g,'').replace(/^52/,'')}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="border-stone-200 h-8 w-8 p-0"><MessageCircle className="w-3.5 h-3.5" /></Button>
                        </a>
                      ) : null}
                      {!pago.cancelado && (
                        <Button size="sm" variant="outline" onClick={() => { setCancelarDialog(pago); setMotivoCancelacion(""); }} className="border-red-200 text-red-600 hover:bg-red-50 h-8 w-8 p-0" title="Cancelar pago" data-testid={`btn-cancelar-pago-${pago.pago_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {pago.cancelado && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700">
                      <p><strong>Cancelado</strong> el {pago.cancelado_at ? new Date(pago.cancelado_at).toLocaleString("es-MX") : "—"} por {pago.cancelado_por_email || "desconocido"}</p>
                      {pago.motivo_cancelacion && <p className="italic mt-0.5">Motivo: {pago.motivo_cancelacion}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <DollarSign className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <h3 className="font-display text-xl text-stone-900 mb-2">Sin pagos</h3>
          <p className="text-stone-500 text-sm">Crea un pago para empezar el seguimiento</p>
        </div>
      )}

      {/* Nuevo Pago Dialog */}
      <Dialog open={pagoDialog} onOpenChange={setPagoDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Nuevo Pago</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select value={newPago.cliente_id} onValueChange={(v) => setNewPago(p => ({ ...p, cliente_id: v }))}>
                <SelectTrigger className="border-stone-300" data-testid="select-cliente-pago">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientesList.map(c => (
                    <SelectItem key={c.cliente_id} value={c.cliente_id}>{c.nombre} - {c.tipo_evento}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Monto Total ($) *</Label>
              <Input type="number" placeholder="Ej: 15000" value={newPago.monto_total} onChange={(e) => setNewPago(p => ({ ...p, monto_total: e.target.value }))} className="border-stone-300" data-testid="input-monto-pago" />
            </div>
            <div className="space-y-2">
              <Label>Monto pagado al crear ($)</Label>
              <Input type="number" placeholder="Si recibiste anticipo, registralo aqui" value={newPago.monto_pagado} onChange={(e) => setNewPago(p => ({ ...p, monto_pagado: e.target.value }))} className="border-stone-300" data-testid="input-monto-pagado-pago" />
              <p className="text-xs text-stone-500">Si dejas en 0, el pago queda como pendiente.</p>
            </div>
            <div className="space-y-2">
              <Label>Método de pago</Label>
              <Select value={newPago.metodo} onValueChange={(v) => setNewPago(p => ({ ...p, metodo: v }))}>
                <SelectTrigger className="border-stone-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Input placeholder="Notas opcionales" value={newPago.notas} onChange={(e) => setNewPago(p => ({ ...p, notas: e.target.value }))} className="border-stone-300" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagoDialog(false)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleCrearPago} className="btn-primary" data-testid="btn-crear-pago">Crear Pago</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registrar Pago Dialog */}
      <Dialog open={!!registrarDialog} onOpenChange={() => setRegistrarDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Registrar Pago</DialogTitle></DialogHeader>
          {registrarDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-stone-50 p-3 rounded-sm text-sm space-y-1">
                <p><strong>{clientes[registrarDialog.cliente_id]?.nombre}</strong></p>
                <p className="text-stone-500">Total: ${(registrarDialog.monto_total || registrarDialog.monto || 0).toLocaleString()}</p>
                <p className="text-stone-500">Pagado: ${(registrarDialog.monto_pagado || 0).toLocaleString()}</p>
                <p className="text-amber-700 font-medium">Restante: ${Math.max(0, (registrarDialog.monto_total || registrarDialog.monto || 0) - (registrarDialog.monto_pagado || 0)).toLocaleString()}</p>
              </div>
              <div className="space-y-2">
                <Label>Monto a registrar ($) *</Label>
                <Input type="number" placeholder="Ej: 5000" value={registrarMonto} onChange={(e) => setRegistrarMonto(e.target.value)} className="border-stone-300" data-testid="input-registrar-monto" />
              </div>
              <div className="space-y-2">
                <Label>Método</Label>
                <Select value={registrarMetodo} onValueChange={setRegistrarMetodo}>
                  <SelectTrigger className="border-stone-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Input placeholder="Notas del pago" value={registrarNotas} onChange={(e) => setRegistrarNotas(e.target.value)} className="border-stone-300" />
              </div>
              <div className="space-y-2">
                <Label>Comprobante (foto del depósito · opcional)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    disabled={subiendoComprobante}
                    onChange={(e) => handleSubirComprobante(e.target.files?.[0])}
                    className="border-stone-300 text-xs"
                    data-testid="input-comprobante-pago"
                  />
                  {subiendoComprobante && <span className="text-xs text-stone-500">Subiendo…</span>}
                </div>
                {registrarComprobante && (
                  <a href={registrarComprobante} target="_blank" rel="noreferrer" className="block w-24 h-24 rounded border border-stone-200 overflow-hidden mt-1">
                    <ImgFallback
                      src={registrarComprobante}
                      alt="Comprobante"
                      className="w-full h-full object-cover"
                      fallback={<div className="w-full h-full flex items-center justify-center text-[10px] text-stone-500">PDF / archivo</div>}
                    />
                  </a>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegistrarDialog(null)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleRegistrarPago} className="btn-primary" data-testid="btn-confirmar-registrar">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Pago Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Editar Pago</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Monto Total ($)</Label>
              <Input type="number" value={editData.monto_total} onChange={(e) => setEditData(p => ({ ...p, monto_total: e.target.value }))} className="border-stone-300" data-testid="input-edit-monto" />
            </div>
            <div className="space-y-2">
              <Label>Metodo de pago</Label>
              <Select value={editData.metodo} onValueChange={(v) => setEditData(p => ({ ...p, metodo: v }))}>
                <SelectTrigger className="border-stone-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Input value={editData.notas} onChange={(e) => setEditData(p => ({ ...p, notas: e.target.value }))} className="border-stone-300" data-testid="input-edit-notas" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleEditPago} className="btn-primary" data-testid="btn-guardar-edit-pago">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancelar Pago Dialog (soft delete con motivo) */}
      <Dialog open={!!cancelarDialog} onOpenChange={() => { setCancelarDialog(null); setMotivoCancelacion(""); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-red-700">Cancelar pago</DialogTitle>
            <DialogDescription>
              El pago no se eliminará. Se marcará como cancelado y permanecerá en el historial con tu nombre, fecha y motivo.
            </DialogDescription>
          </DialogHeader>
          {cancelarDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-stone-50 p-3 rounded-sm text-sm space-y-1">
                <p><strong>{clientes[cancelarDialog.cliente_id]?.nombre || "Cliente"}</strong></p>
                <p className="text-stone-500">Total: ${(cancelarDialog.monto_total || cancelarDialog.monto || 0).toLocaleString()}</p>
                <p className="text-stone-500">Pagado: ${(cancelarDialog.monto_pagado || 0).toLocaleString()}</p>
              </div>
              <div className="space-y-2">
                <Label>Motivo de cancelación *</Label>
                <Textarea
                  value={motivoCancelacion}
                  onChange={(e) => setMotivoCancelacion(e.target.value)}
                  placeholder="Ej: Cliente solicitó devolución, error de captura, pago duplicado…"
                  rows={3}
                  className="border-stone-300"
                  data-testid="textarea-motivo-cancelacion"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelarDialog(null); setMotivoCancelacion(""); }} className="border-stone-300">Volver</Button>
            <Button onClick={handleCancelarPago} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-confirmar-cancelar-pago">
              Cancelar pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historial Dialog */}
      <Dialog open={!!historialDialog} onOpenChange={() => setHistorialDialog(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Historial del pago</DialogTitle>
            <DialogDescription>
              {historialDialog ? `${clientes[historialDialog.cliente_id]?.nombre || ""} · ${historialDialog.pago_id}` : ""}
            </DialogDescription>
          </DialogHeader>
          {historialDialog && (
            <HistorialPanel coleccion="pagos" docId={historialDialog.pago_id} titulo="Movimientos" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistorialDialog(null)} className="border-stone-300">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pagos;
